import {
  Badge,
  Box,
  Button,
  CopyButton,
  Grid,
  Group,
  SegmentedControl,
  Select,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconCopy, IconDeviceFloppy, IconInfoCircle, IconMail, IconPhone, IconTrash } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import Regex from '../../constants/Regex';
import getMergeTags from '../../constants/getMergeTags';
import viberTemplateLanguages, {
  templateStatusColor,
  viberTemplateSenders,
} from '../../constants/viberTemplateLanguages';
import useFetch from '../../helpers/useFetch';
import { validateHandlebarsValue } from '../../helpers/validateHandlebars';
import { validateUrlProtocol } from '../../helpers/validateURL';
import AssetPickerModal from '../Assets/AssetPickerModal';
import DailyLimitSettings from '../DailyLimitSettings';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import MergeTagsCollapsible from '../MergeTagsCollapsible';
import ConfirmationModal from '../Modals/ConfirmationModal';
import SendTestMessageModal from '../Modals/SendTestMessageModal';
import ProductFeedSelector from '../ProductFeeds/ProductFeedSelector';
import Spinner from '../Spinner';
import WarningMessageOnDelete from '../WarningMessageOnDelete';

const ViberMessageForm = ({
  viberMessage: passedViberMessage,
  saveViberMessage,
  deleteViberMessage,
  showUserFlow,
  deleteError,
  onBack,
  allowSaveAndSubmit = false,
}) => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { domain } = useSelector((state) => state.domain);
  // Mobica has no template-create API (yet): its templates are created in the Mobica portal from text
  // Releva generates, then linked back by id - a different UI flow from LM/Routee's one-click submit.
  const isMobica = domain?.viberMessageSender === 'mobica';
  const supportsTemplates = viberTemplateSenders.includes(domain?.viberMessageSender);

  const [dynamicFieldsOpen, setDynamicFieldsOpen] = useState(false);
  const [testViberMessageOpen, setTestViberMessageOpen] = useState(false);
  const [customFields, setCustomFields] = useState();
  const [viberMessage, setViberMessage] = useState(passedViberMessage);
  const [error, setError] = useState();
  // Template submit/refresh errors are kept separate from `error` so they never leak into the
  // unrelated test-message modal (which is also fed `error`).
  const [templateError, setTemplateError] = useState();
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [isValidBtnText, setIsValidBtnText] = useState(true);
  const [isValidBtnUrl, setIsValidBtnUrl] = useState(true);
  const [coupons, setCoupons] = useState();
  const [surveys, setSurveys] = useState([]);
  const [messagePreview, setMessagePreview] = useState('');
  // Preview-fetch failures get their own state so they render under the Preview section instead of
  // leaking into the shared `error` (which also feeds the unrelated test-message modal).
  const [previewError, setPreviewError] = useState();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  // Mobica has no template-create API yet: Releva generates the exact template text to copy into the
  // Mobica portal, then the portal-assigned numeric id is linked back here.
  const [mobicaTemplateText, setMobicaTemplateText] = useState('');
  const [mobicaTemplateId, setMobicaTemplateId] = useState('');

  const { accountId, domainId } = useParams();

  const maxMessageLength = viberMessage?.type === 'recommender' ? 5000 : 7000;

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/customFields`)
      .then(({ customFields }) => setCustomFields(customFields))
      .catch(setLoadError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/coupons`)
      .then(({ coupons }) => setCoupons(coupons))
      .catch(setLoadError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/surveys`)
      .then(({ surveys }) => setSurveys(surveys))
      .catch(setLoadError);
  }, [accountId, domainId, fetch]);

  const fetchMessagePreview = useCallback(
    async (message) => {
      try {
        setLoading(true);
        const data = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/shortenedUrlMessage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ message }),
        });
        setMessagePreview(data.shortenedMessage);
        setPreviewError(undefined);
      } catch (error) {
        setPreviewError(error);
      } finally {
        setLoading(false);
      }
    },
    [accountId, domainId, fetch]
  );

  // Build the debounced fetch once (per fetcher identity) so keystrokes are actually coalesced instead
  // of a fresh 500ms timer being created on every message change.
  const debouncedFetchMessagePreview = useMemo(
    () => AwesomeDebouncePromise(fetchMessagePreview, 500),
    [fetchMessagePreview]
  );

  useEffect(() => {
    if (viberMessage && viberMessage.message) {
      debouncedFetchMessagePreview(viberMessage.message);
    } else {
      // Clearing the message must also clear a stale preview/error so they don't linger from a prior value.
      setMessagePreview('');
      setPreviewError(undefined);
    }
  }, [viberMessage, debouncedFetchMessagePreview]);

  // Transactional Viber must be text-only (no button/image/URL); a button or image makes it promotional.
  // Coerce a new transactional message back to text mode and drop any multimedia fields.
  useEffect(() => {
    if (
      viberMessage?.isPromotional === false &&
      !viberMessage?.id &&
      (viberMessage?.mode === 'multimedia' || viberMessage?.btnUrl || viberMessage?.btnText || viberMessage?.imageUrl)
    ) {
      setViberMessage((current) => ({ ...current, mode: 'text', btnUrl: '', btnText: '', imageUrl: '' }));
    }
  }, [
    viberMessage?.isPromotional,
    viberMessage?.id,
    viberMessage?.mode,
    viberMessage?.btnUrl,
    viberMessage?.btnText,
    viberMessage?.imageUrl,
  ]);

  const handleChange = (key, value) => {
    setViberMessage({ ...viberMessage, [key]: value });
  };

  // Viber rejects templates whose lang isn't a supported locale. Fall back to English for legacy/unknown
  // codes so the language shown in the picker and the one submitted for approval can never diverge.
  const normalizeTemplateLang = (lang) =>
    viberTemplateLanguages.some((l) => l.value === lang) ? lang : 'en';

  // Returns the full response: { viberMessage, templateText? } - templateText is only present for
  // Mobica, whose templates are created in its portal from the generated text.
  const submitTemplateRequest = async (id) => {
    return await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages/${id}/submitTemplate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lang: normalizeTemplateLang(viberMessage.templateLang) }),
    });
  };

  // Flow editor: the message is saved separately (and its modal closes on save), so this only submits
  // the already-saved message.
  const submitTemplate = async () => {
    setTemplateError(undefined);
    try {
      setTemplateLoading(true);
      const { viberMessage: updated } = await submitTemplateRequest(viberMessage.id);
      setViberMessage((current) => ({ ...current, ...updated }));
    } catch (e) {
      setTemplateError(e);
    } finally {
      setTemplateLoading(false);
    }
  };

  // Mobica step 1: validate + build the template server-side and show the exact text (single-brace
  // {param} syntax) the user must create in the Mobica portal.
  const generateMobicaTemplate = async () => {
    setTemplateError(undefined);
    try {
      setTemplateLoading(true);
      const { viberMessage: updated, templateText } = await submitTemplateRequest(viberMessage.id);
      setViberMessage((current) => ({ ...current, ...updated }));
      setMobicaTemplateText(templateText || '');
    } catch (e) {
      setTemplateError(e);
    } finally {
      setTemplateLoading(false);
    }
  };

  // Mobica step 2: link the portal-assigned template id - the backend verifies it via Mobica's
  // check endpoint and stores the id + its current moderation status.
  const linkMobicaTemplate = async () => {
    setTemplateError(undefined);
    try {
      setTemplateLoading(true);
      const { viberMessage: updated } = await fetch(
        `/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages/${viberMessage.id}/linkTemplate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ templateId: mobicaTemplateId }),
        }
      );
      setViberMessage((current) => ({ ...current, ...updated }));
      setMobicaTemplateText('');
      setMobicaTemplateId('');
    } catch (e) {
      setTemplateError(e);
    } finally {
      setTemplateLoading(false);
    }
  };

  // Standalone editor: one action that persists the message (skipping the parent's post-save redirect)
  // and then submits it for Viber approval, so the user stays on the form and sees the resulting status.
  const saveAndSubmitTemplate = async () => {
    setTemplateError(undefined);
    setTemplateLoading(true);
    try {
      const saved = await saveViberMessage(viberMessage, { skipPostSave: true });
      if (saved) {
        setViberMessage((current) => ({ ...current, ...saved }));
      }
      const id = saved?.id ?? viberMessage.id;
      if (!id) {
        throw new Error('Could not save the message before submitting it for approval.');
      }
      // Only (re)submit when there is no usable template yet - never reset an APPROVED/PENDING one.
      // Editing the body/language clears templateId server-side, so an edited message resubmits here.
      const current = saved ?? viberMessage;
      if (!current.templateId || current.templateStatus === 'REJECTED') {
        const { viberMessage: updated } = await submitTemplateRequest(id);
        setViberMessage((cur) => ({ ...cur, ...updated }));
      }
    } catch (e) {
      setTemplateError(e);
    } finally {
      setTemplateLoading(false);
    }
  };

  const refreshTemplateStatus = async () => {
    setTemplateError(undefined);
    try {
      setTemplateLoading(true);
      const { viberMessage: updated } = await fetch(
        `/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages/${viberMessage.id}/refreshTemplateStatus`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      setViberMessage((current) => ({ ...current, ...updated }));
    } catch (e) {
      setTemplateError(e);
    } finally {
      setTemplateLoading(false);
    }
  };

  const validateButtonFields = (btnText, btnUrl) => {
    const isBtnTextValid = !btnUrl || !!btnText;

    let isBtnUrlValid = true;
    if (btnText) {
      isBtnUrlValid = !!btnUrl && (Regex.mergeTagsRegex.test(btnUrl) || validateUrlProtocol(btnUrl));
    } else if (btnUrl) {
      isBtnUrlValid = Regex.mergeTagsRegex.test(btnUrl) || validateUrlProtocol(btnUrl);
    }

    setIsValidBtnText(isBtnTextValid);
    setIsValidBtnUrl(isBtnUrlValid);
  };

  const handleButtonChange = (name, value) => {
    const updatedBtnText = name === 'btnText' ? value : viberMessage.btnText;
    const updatedBtnUrl = name === 'btnUrl' ? value : viberMessage.btnUrl;

    validateButtonFields(updatedBtnText, updatedBtnUrl);
    if (name === 'btnUrl') {
      setIsValidUrl(!value || Regex.mergeTagsRegex.test(value) || validateUrlProtocol(value));
    }

    handleChange(name, value);
  };

  const send = (sendTo, impersonateProfileQuery, triggerEventAction, cb) => {
    setError(undefined);
    setSending(true);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages/testViberMessage`, {
      method: 'POST',
      body: JSON.stringify({
        sendTo,
        impersonateProfileQuery,
        triggerEventAction,
        viberMessage,
      }),
    })
      .then(cb)
      .catch(setError)
      .finally(() => setSending(false));
    return false;
  };

  if (viberMessage === undefined || !customFields) {
    return <Spinner />;
  }

  if (viberMessage === null) {
    return <ErrorMessage message="Viber message does not exist!" />;
  }

  const messageHandlebarsError = validateHandlebarsValue(viberMessage.message);
  const btnUrlHandlebarsError = validateHandlebarsValue(viberMessage.btnUrl);

  const isValid =
    viberMessage.name &&
    viberMessage.message &&
    viberMessage.message?.length <= maxMessageLength &&
    isValidUrl &&
    (viberMessage.type !== 'recommender' || viberMessage.productFeedId) &&
    isValidBtnText &&
    isValidBtnUrl &&
    !messageHandlebarsError &&
    !btnUrlHandlebarsError;

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  const isTransactional = viberMessage.isPromotional === false;
  // Standalone pages opt in to the single "Save & submit for approval" button. The flow editor saves the
  // message separately (and closes its modal on save), so it keeps the two-step save + submit controls.
  // Mobica is excluded: its template is created in the Mobica portal (generate text -> link id), so a
  // combined save-and-submit has nothing to submit to.
  const useCombinedSubmit = allowSaveAndSubmit && supportsTemplates && isTransactional && !isMobica;
  // A template awaiting moderation or already approved is frozen on Viber's side - lock its text so it
  // can't be silently edited (which would invalidate the approval). Rejected templates stay editable so
  // the user can fix and resubmit.
  const templateLocked =
    supportsTemplates && isTransactional && !!viberMessage.templateId && viberMessage.templateStatus !== 'REJECTED';

  let modeConfig;
  if (viberMessage.mode === 'multimedia') {
    modeConfig = (
      <>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }}>
            <Text size="sm" fw={500}>
              Type
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <SegmentedControl
              disabled={!!viberMessage.id}
              value={viberMessage.type ?? ''}
              onChange={(type) => {
                let change = {};
                if (type === 'recommender') {
                  if (!viberMessage.btnText) {
                    change.btnText = 'Shop Now';
                  }
                  if (!viberMessage.btnUrl) {
                    change.btnUrl = '{{product.url}}';
                  }
                  if (!viberMessage.btnUrl) {
                    change.imageUrl = '{{product.imageUrl}}';
                  }
                  if (!viberMessage.message) {
                    change.message = `Hi {{firstName}},

Only for a limited time!

{{product.name}} - {{formatPrice product "price"}}`;
                  }
                } else if (type === 'message') {
                  change.btnUrl = '';
                  change.btnText = '';
                  change.imageUrl = '';
                  change.productFeed = null;
                }

                setViberMessage({ ...viberMessage, ...change, type });
              }}
              data={[
                { label: 'Message', value: 'message' },
                { label: 'Recommender', value: 'recommender' },
              ]}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }}>
            <Text size="sm" fw={500}>
              Button Text
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <TextInput
              name="btnText"
              type="text"
              value={viberMessage.btnText}
              placeholder="e.g. Check This Out!"
              error={!isValidBtnText ? 'Button Text is required when Button URL is provided.' : null}
              onChange={(e) => handleButtonChange(e.target.name, e.target.value)}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }}>
            <Text size="sm" fw={500}>
              Button URL
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <TextInput
              name="btnUrl"
              type="text"
              value={viberMessage.btnUrl || ''}
              placeholder={`e.g. ${domain.url}`}
              error={
                btnUrlHandlebarsError ||
                (!isValidUrl || !isValidBtnUrl
                  ? !isValidUrl
                    ? 'Invalid URL format. Example: https://example.com'
                    : 'Button URL is required when Button Text is provided.'
                  : null)
              }
              onChange={(e) => handleButtonChange(e.target.name, e.target.value)}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }}>
            <Text size="sm" fw={500}>
              Image URL
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <TextInput
              name="imageUrl"
              type="text"
              value={viberMessage.imageUrl || ''}
              placeholder="e.g. https://google.com/panda.jpg"
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              rightSection={<Button onClick={() => setImagePickerOpen(true)}>Browse</Button>}
              rightSectionWidth={80}
            />
            <AssetPickerModal
              key="viber-asset"
              show={imagePickerOpen}
              close={() => setImagePickerOpen(false)}
              confirm={(asset, _, cb) => {
                setViberMessage({ ...viberMessage, imageUrl: asset.url });
                cb();
              }}
            />
            <Text size="sm" c="dimmed">
              <div>Supported image formats: JPEG, JPG, PNG. </div>
              <div>Recommended image thumbnail size is 400x400.</div>
            </Text>
          </Grid.Col>
        </Grid>
      </>
    );
  }

  let deleteButton;
  if (deleteViberMessage) {
    deleteButton = (
      <LockableButton restsrictionKey="viberMessagesDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  let duplicateButton;
  if (viberMessage.id) {
    duplicateButton = (
      <LockableButton
        restrictionKey="viberMessagesCreate"
        color="cyan"
        onClick={() => {
          navigate(
            location.pathname.replace(`/viber/${viberMessage.id}/details`, `/viber/new?from=${viberMessage.id}`)
          );
        }}
      >
        <IconCopy size={14} /> Make a copy
      </LockableButton>
    );
  }

  const testMessageButton = (
    <Button
      color="cyan"
      size="sm"
      disabled={!isValid}
      onClick={() => {
        setTestViberMessageOpen(true);
      }}
    >
      <IconPhone size={14} /> Test Message
    </Button>
  );

  return (
    <>
      <SendTestMessageModal
        send={send}
        close={() => setTestViberMessageOpen(false)}
        error={error}
        sending={sending}
        show={testViberMessageOpen}
        sendToPlaceholder="Paste phone numbers (with +<countryCode>) separated by ','"
        sendTo={user.phoneNumber}
        isSendToValid={(sendTo) => sendTo.split(',').every((v) => Regex.phoneNumberRegex.test(v))}
      />
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Name*
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <TextInput
            name="name"
            type="text"
            value={viberMessage.name}
            placeholder="Test Viber Message"
            onChange={(e) => handleChange(e.target.name, e.target.value)}
          />
        </Grid.Col>
      </Grid>
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Scope*
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <SegmentedControl
            disabled={!!viberMessage.id}
            value={String(viberMessage.isPromotional)}
            onChange={(value) => handleChange('isPromotional', value === 'true')}
            data={[
              { label: 'Marketing', value: 'true' },
              { label: 'Transactional', value: 'false' },
            ]}
          />
          <Text size="sm" c="dimmed">
            <div>
              <strong>Transactional messages</strong> allow you to notify your customers of any changes or important
              notifications they should be aware of (e.g. account charges, booking confirmations, feedback, etc.).
            </div>
            <div>
              <strong>Marketing messages</strong> allow you to use Viber as a commercial boosting channel.
            </div>
          </Text>
        </Grid.Col>
      </Grid>
      {supportsTemplates && viberMessage.isPromotional === false && (
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }}>
            <Text size="sm" fw={500}>
              Viber template
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <Group gap="sm" mb="xs">
              <Badge color={templateStatusColor(viberMessage.templateStatus)}>
                {viberMessage.templateStatus || 'Not submitted'}
              </Badge>
              {(!viberMessage.templateId || viberMessage.templateStatus === 'REJECTED') &&
                (isMobica ? (
                  <Button
                    size="xs"
                    variant="light"
                    loading={templateLoading}
                    disabled={!viberMessage.id}
                    onClick={generateMobicaTemplate}
                  >
                    Generate template text
                  </Button>
                ) : (
                  <>
                    <Select
                      size="xs"
                      w={180}
                      data={viberTemplateLanguages}
                      value={normalizeTemplateLang(viberMessage.templateLang)}
                      onChange={(value) => handleChange('templateLang', value || 'en')}
                      allowDeselect={false}
                      searchable
                      nothingFoundMessage="No language"
                      comboboxProps={{ withinPortal: true }}
                    />
                    {!useCombinedSubmit && (
                      <Button
                        size="xs"
                        variant="light"
                        loading={templateLoading}
                        disabled={!viberMessage.id}
                        onClick={submitTemplate}
                      >
                        Submit for approval
                      </Button>
                    )}
                  </>
                ))}
              {viberMessage.templateId && (
                <Button size="xs" variant="subtle" loading={templateLoading} onClick={refreshTemplateStatus}>
                  Refresh status
                </Button>
              )}
            </Group>
            {isMobica && mobicaTemplateText && (!viberMessage.templateId || viberMessage.templateStatus === 'REJECTED') && (
              <>
                <Textarea readOnly rows={3} value={mobicaTemplateText} mb="xs" />
                <Group gap="sm" mb="xs">
                  <CopyButton value={mobicaTemplateText}>
                    {({ copied, copy }) => (
                      <Button size="xs" variant="default" onClick={copy}>
                        {copied ? 'Copied' : 'Copy text'}
                      </Button>
                    )}
                  </CopyButton>
                  <TextInput
                    size="xs"
                    w={160}
                    placeholder="Template ID"
                    value={mobicaTemplateId}
                    onChange={(e) => setMobicaTemplateId(e.target.value.replace(/\D/g, ''))}
                  />
                  <Button
                    size="xs"
                    variant="light"
                    loading={templateLoading}
                    disabled={!mobicaTemplateId}
                    onClick={linkMobicaTemplate}
                  >
                    Link template
                  </Button>
                </Group>
                <Text size="sm" c="dimmed">
                  Create this exact template in the Mobica portal, then enter the template ID you receive and link it.
                </Text>
              </>
            )}
            {!useCombinedSubmit && !viberMessage.id && (
              <Text size="sm" c="dimmed">
                {isMobica
                  ? 'Save the message first, then generate the template text for the Mobica portal.'
                  : 'Save the message first, then submit it for Viber approval.'}
              </Text>
            )}
            {viberMessage.templateRejectionReason && (
              <Text size="sm" c="red">
                Rejected: {viberMessage.templateRejectionReason}
              </Text>
            )}
            {templateError && <ErrorMessage message={templateError.message} />}
            <Text size="sm" c="dimmed">
              {isMobica
                ? 'From 15 July 2026 Viber requires an approved template for transactional messages, otherwise they are billed as promotional. Generate the template text, create it in the Mobica portal, then link the template ID here; once approved it is sent as a template.'
                : 'From 15 July 2026 Viber requires an approved template for transactional messages, otherwise they are billed as promotional. Submit this message for approval; once approved it is sent as a template.'}
            </Text>
          </Grid.Col>
        </Grid>
      )}
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Mode
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <SegmentedControl
            disabled={!!viberMessage.id}
            value={viberMessage.mode ?? ''}
            onChange={(mode) => handleChange('mode', mode)}
            data={[
              { label: 'Text', value: 'text' },
              { label: 'Multimedia', value: 'multimedia', disabled: viberMessage.isPromotional === false },
            ]}
          />
          {viberMessage.isPromotional === false && (
            <Text size="sm" c="dimmed" mt="xs">
              Transactional messages are text-only buttons and images are available only for Marketing messages.
            </Text>
          )}
        </Grid.Col>
      </Grid>
      {modeConfig}
      {viberMessage.type === 'recommender' && (
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 2 }}>
            <Text size="sm" fw={500}>
              Product Feed*
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <ProductFeedSelector
              accountId={accountId}
              domainId={domainId}
              productFeedId={viberMessage.productFeedId}
              onSelect={(productFeedId) => setViberMessage({ ...viberMessage, productFeedId })}
              scope="marketingChannels"
              forType="viber message"
            />
          </Grid.Col>
        </Grid>
      )}
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Message*
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <Group justify="flex-end" gap="xs" py="md">
            <Button
              size="sm"
              variant="default"
              onClick={() => setDynamicFieldsOpen(!dynamicFieldsOpen)}
              aria-expanded={dynamicFieldsOpen}
            >
              <IconInfoCircle size={14} /> Merge Tags
            </Button>
            {testMessageButton}
          </Group>
          <MergeTagsCollapsible
            open={dynamicFieldsOpen}
            mergeTags={getMergeTags(customFields, coupons, surveys, {
              includeProduct: viberMessage.type === 'recommender',
            })}
          />

          <Textarea
            rows={5}
            onChange={(e) => handleChange(e.target.name, e.target.value)}
            name="message"
            value={viberMessage.message}
            error={messageHandlebarsError}
            disabled={templateLocked}
          />
          {templateLocked && (
            <Text size="sm" c="dimmed" fw={700} mt="xs">
              This message has been submitted as a Viber template and can no longer be edited. To change the text, use
              “Make a copy” and submit the new message for approval.
            </Text>
          )}

          <Text size="sm" c="dimmed">
            <div>
              Length: {messagePreview.length ? messagePreview.length : viberMessage.message?.length || 0} /{' '}
              {maxMessageLength}
            </div>
            <div>Messages can contain up to {maxMessageLength} characters. Emojis are also supported.</div>
            <div>
              Please note that links will be shortened. Actual link is provided in the <strong>Preview</strong> section
              down below.
            </div>
            <div>
              If the message includes merge tags like{' '}
              <strong>
                <code className="merge-tags">&#123;&#123;firstName&#125;&#125;</code>
              </strong>{' '}
              it might exceed the character limit and may require sending in multiple messages. Example: John (4 chars)
              Alexander (9 chars).
            </div>
          </Text>
        </Grid.Col>
      </Grid>
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Preview
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          {loading ? (
            <Spinner />
          ) : (
            <Textarea
              disabled
              rows={5}
              name="message"
              value={messagePreview ? messagePreview : 'No message preview available'}
            />
          )}
          {previewError && <ErrorMessage message={previewError.message} />}
        </Grid.Col>
      </Grid>
      <DailyLimitSettings
        model={viberMessage}
        onChange={(key, value) => handleChange(key, value)}
        channelLabel="viber messages"
        maxPerDay={domain.maxViberMessagesPerDay}
        accountId={accountId}
        domainId={domainId}
        settingsTab="viberMessage"
      />
      {error && <ErrorMessage message={error.message} />}
      <Group justify="flex-end" gap="xs" py="md">
        <Button variant="default" onClick={onBack}>
          Back
        </Button>
        {deleteButton}
        {duplicateButton}
        {showUserFlow && (
          <Button variant="default" onClick={() => showUserFlow()}>
            <IconMail size={14} /> Send
          </Button>
        )}
        {useCombinedSubmit ? (
          <LockableButton
            restrictionKey={`viberMessages${viberMessage.id ? 'Update' : 'Create'}`}
            onClick={saveAndSubmitTemplate}
            disabled={!isValid}
            loading={templateLoading}
          >
            <IconDeviceFloppy size={14} /> {templateLocked ? 'Save' : 'Save & submit for approval'}
          </LockableButton>
        ) : (
          <LockableButton
            restrictionKey={`viberMessages${viberMessage.id ? 'Update' : 'Create'}`}
            onClick={() => saveViberMessage(viberMessage)}
            disabled={!isValid}
          >
            <IconDeviceFloppy size={14} /> Save
          </LockableButton>
        )}
      </Group>
      <ConfirmationModal
        show={deleteError ? false : confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        confirm={(cb) => deleteViberMessage(cb)}
        message={<WarningMessageOnDelete type="Viber Message" />}
      />
      {deleteError ? (
        <Box ta="right">
          <ErrorMessage message={deleteError} />
        </Box>
      ) : null}
    </>
  );
};

export default ViberMessageForm;
